import React from 'react'
import { StyleProp, TouchableOpacity, View, ViewStyle } from 'react-native'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Icon, IconName, Text } from '@src/components/base'
import { MarkdownView } from '@src/components/Markdown'
import { PromoBadge } from '@src/components/PromoBadge'

type PromoBannerProps = {
  badgeText?: string
  style?: StyleProp<ViewStyle>
  onPress: () => void
  accessibilityLabel: string
  title: string
  descriptionMarkdown: string
  iconName: IconName
  iconStyle?: StyleProp<ViewStyle>
} & (
  | {
      onIconPress: () => void
      iconAccessibilityLabel: string
    }
  | {
      onIconPress?: never
      iconAccessibilityLabel?: never
    }
)

export const PromoBanner = (props: PromoBannerProps) => {
  const {
    badgeText,
    style,
    onPress,
    accessibilityLabel,
    title,
    descriptionMarkdown,
    iconName,
    iconStyle,
    onIconPress,
    iconAccessibilityLabel,
  } = props
  const styles = useStyleSheet(themedStyle)

  const icon = <Icon name={iconName} size="16" weight="bold" style={[styles.icon, iconStyle]} />

  return (
    <TouchableOpacity
      onPress={onPress}
      style={[styles.container, style]}
      accessibilityLabel={accessibilityLabel}
    >
      <PromoBadge badgeText={badgeText} size={56} fontSize={13} style={styles.badge} />
      <View style={styles.descriptionContainer}>
        <Text type="regular" bold style={styles.primaryText}>
          {title}
        </Text>
        <MarkdownView style={styles.markdownView} content={descriptionMarkdown} />
      </View>
      {onIconPress ? (
        <TouchableOpacity
          onPress={onIconPress}
          hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
          accessibilityLabel={iconAccessibilityLabel}
        >
          {icon}
        </TouchableOpacity>
      ) : (
        icon
      )}
    </TouchableOpacity>
  )
}

const themedStyle = StyleService.create({
  container: {
    flexDirection: 'row',
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'theme.surface.base2',
  },
  badge: {
    marginHorizontal: 12,
  },
  icon: {
    color: 'theme.text.primary',
    marginHorizontal: 16,
  },
  descriptionContainer: {
    flex: 1,
  },
  markdownView: {
    marginBottom: -16, // remove extra margin after paragraph
  },
  primaryText: {
    color: 'theme.text.primary',
  },
})
